import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config/api';

const STEP_UP_STORAGE_KEY = 'fundelite_provider_credential_step_up';
const REVEAL_TTL_MS = 60 * 1000;

export const REVEAL_UNAVAILABLE_MESSAGE =
  'Could not reveal saved credential. You can still use saved credentials without viewing them.';

function readStepUpSession() {
  try {
    const raw = sessionStorage.getItem(STEP_UP_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token || !parsed?.expiresAt) return null;
    if (Date.now() > parsed.expiresAt) {
      sessionStorage.removeItem(STEP_UP_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStepUpSession(token, expiresInSeconds = 300) {
  sessionStorage.setItem(
    STEP_UP_STORAGE_KEY,
    JSON.stringify({
      token,
      expiresAt: Date.now() + expiresInSeconds * 1000,
    }),
  );
}

export function clearProviderCredentialRevealSession() {
  sessionStorage.removeItem(STEP_UP_STORAGE_KEY);
}

function resolveRevealErrorMessage(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status === 404) {
    return REVEAL_UNAVAILABLE_MESSAGE;
  }
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return REVEAL_UNAVAILABLE_MESSAGE;
}

/**
 * Secure reveal hook for saved provider password / security answer fields.
 */
export function useProviderCredentialReveal(provider) {
  const [revealedValues, setRevealedValues] = useState({});
  const [revealLoading, setRevealLoading] = useState(null);
  const [revealError, setRevealError] = useState(null);
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const [pendingField, setPendingField] = useState(null);
  const hideTimersRef = useRef({});

  const clearFieldReveal = useCallback((field) => {
    setRevealedValues((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    if (hideTimersRef.current[field]) {
      clearTimeout(hideTimersRef.current[field]);
      delete hideTimersRef.current[field];
    }
  }, []);

  const clearAllReveals = useCallback(() => {
    Object.values(hideTimersRef.current).forEach(clearTimeout);
    hideTimersRef.current = {};
    setRevealedValues({});
    setRevealError(null);
  }, []);

  useEffect(() => () => {
    Object.values(hideTimersRef.current).forEach(clearTimeout);
  }, []);

  const scheduleAutoHide = useCallback((field, ttlMs = REVEAL_TTL_MS) => {
    if (hideTimersRef.current[field]) clearTimeout(hideTimersRef.current[field]);
    hideTimersRef.current[field] = setTimeout(() => clearFieldReveal(field), ttlMs);
  }, [clearFieldReveal]);

  const callRevealApi = useCallback(async (field, fundelitePassword = null) => {
    const token = localStorage.getItem('fundelite_token');
    const stepUp = readStepUpSession();
    const payload = {
      provider,
      field,
      purpose: 'user_reveal_saved_provider_credential',
    };
    if (fundelitePassword) {
      payload.fundelitePassword = fundelitePassword;
    } else if (stepUp?.token) {
      payload.stepUpToken = stepUp.token;
    }

    const response = await axios.post(`${API}/provider-credentials/reveal`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }, [provider]);

  const revealField = useCallback(async (field, fundelitePassword = null) => {
    if (!provider || !field) return null;
    setRevealLoading(field);
    setRevealError(null);
    try {
      const data = await callRevealApi(field, fundelitePassword);
      if (data?.stepUpToken) {
        writeStepUpSession(data.stepUpToken, data.stepUpExpiresInSeconds || 300);
      }
      const value = data?.revealedValue ?? null;
      if (value != null) {
        setRevealedValues((prev) => ({ ...prev, [field]: value }));
        scheduleAutoHide(field, (data.expiresInSeconds || 60) * 1000);
      }
      return value;
    } catch (error) {
      const message = resolveRevealErrorMessage(error);
      setRevealError(message);
      throw error;
    } finally {
      setRevealLoading(null);
      setPendingField(null);
      setStepUpOpen(false);
    }
  }, [provider, callRevealApi, scheduleAutoHide]);

  const requestReveal = useCallback(async (field, { canReveal = true } = {}) => {
    if (!canReveal) return;
    if (revealedValues[field]) {
      clearFieldReveal(field);
      return;
    }
    const stepUp = readStepUpSession();
    if (stepUp?.token) {
      try {
        await revealField(field);
      } catch {
        setPendingField(field);
        setStepUpOpen(true);
      }
      return;
    }
    setRevealError(null);
    setPendingField(field);
    setStepUpOpen(true);
  }, [revealedValues, revealField, clearFieldReveal]);

  const handleStepUpSubmit = useCallback(async (fundelitePassword) => {
    if (!pendingField) return;
    await revealField(pendingField, fundelitePassword);
  }, [pendingField, revealField]);

  const cancelStepUp = useCallback(() => {
    setStepUpOpen(false);
    setPendingField(null);
    setRevealError(null);
  }, []);

  return {
    revealedValues,
    revealLoading,
    revealError,
    stepUpOpen,
    pendingField,
    requestReveal,
    handleStepUpSubmit,
    cancelStepUp,
    clearFieldReveal,
    clearAllReveals,
    isRevealed: (field) => Boolean(revealedValues[field]),
    getDisplayValue: (field, maskedValue) => (
      revealedValues[field] != null ? revealedValues[field] : maskedValue
    ),
  };
}

export default useProviderCredentialReveal;
